import { gql, useMutation, useQuery } from '@apollo/client';
import IconMarkdownSolid from '@apollo/icons/default/IconMarkdownSolid.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import * as Popper from '@popperjs/core';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import * as Yup from 'yup';

import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { API_NAME_MAX_CHAR } from 'src/lib/graphOnboarding/constants';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';

import {
  graphProposalListRouteConfig,
  graphRouteConfig,
  proposalEditorRouteConfig,
  variantProposalListRouteConfig,
  variantRouteConfig,
} from '../../routes';
import { addSchemaModalErrorMessages } from '../../subgraphsLayout/subgraphsPage/addSubgraphModal/shared';

const validationSchema = Yup.object().shape({
  // this is used to make subgraphName optional
  shouldPromptForNewSubgraphName: Yup.boolean(),

  displayName: Yup.string().required(
    'Please provide a title for your proposal.',
  ),
  sourceVariantName: Yup.string().required(
    'Please provide a source variant for your proposal.',
  ),
  newSubgraphName: Yup.string().when(
    'shouldPromptForNewSubgraphName',
    (shouldPromptForNewSubgraphName) =>
      shouldPromptForNewSubgraphName
        ? Yup.string()
            .required('Please provide a name for your proposed Subgraph.')
            .min(2, addSchemaModalErrorMessages.subgraphName.min)
            .max(
              API_NAME_MAX_CHAR,
              addSchemaModalErrorMessages.subgraphName.max,
            )
            .matches(
              /^[a-zA-Z]/,
              addSchemaModalErrorMessages.subgraphName.startWithLetter,
            )
            .matches(
              /^[a-zA-Z0-9_-]*$/,
              addSchemaModalErrorMessages.subgraphName.contents,
            )
        : Yup.string(),
  ),
  description: Yup.string(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ProposeChangesModal = ({
  isOpen,
  onClose,
  finalFocusRef,
  existingSubgraphNames,
  proposalDescriptionTemplate,
}: {
  isOpen: boolean;
  onClose: () => void;
  finalFocusRef?: React.RefObject<HTMLButtonElement>;
  existingSubgraphNames?: string[] | Record<string, string[]>;
  proposalDescriptionTemplate?: string;
}) => {
  const [errorMessage, setErrorMessage] = useState('');

  const { graphId, graphVariant } = useRouteParams(
    variantRouteConfig,
    graphRouteConfig,
  );

  const {
    selectedSchema: selectedSchemaFromQueryParams,
    shouldPromptForNewSubgraphName:
      shouldPromptForNewSubgraphNameFromQueryParams,
  } = useRouteParams(
    graphProposalListRouteConfig,
    variantProposalListRouteConfig,
    catchAllRouteConfig,
  );

  const shouldPromptForNewSubgraphName =
    shouldPromptForNewSubgraphNameFromQueryParams === 'true';

  const history = useHistory();

  const [createProposal] = useMutation<
    GraphQLTypes.ProposeChangesModalCreateProposalMutation,
    GraphQLTypes.ProposeChangesModalCreateProposalMutationVariables
  >(gql`
    # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
    mutation ProposeChangesModalCreateProposalMutation(
      $graphId: ID!
      $input: CreateProposalInput!
    ) {
      graph(id: $graphId) {
        createProposal(input: $input) {
          ... on GraphVariant {
            id
            name
          }
          ... on CreateProposalError {
            message
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  // query for list of federated variants here and skip if the prior value is false
  const { data, loading, error } = useQuery<
    GraphQLTypes.ProposeChangesModalQuery,
    GraphQLTypes.ProposeChangesModalQueryVariables
  >(
    gql`
      query ProposeChangesModalQuery($graphId: ID!) {
        graph(id: $graphId) {
          id
          name
          variants {
            id
            name
            hasManagedSubgraphs
          }
        }
      }
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        graphId,
      },
    },
  );

  const federatedSourceVariants = data?.graph?.variants?.filter(
    (variant) => variant.hasManagedSubgraphs,
  );

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik<FormValues>({
    validateOnBlur: false,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      shouldPromptForNewSubgraphName,
      displayName: '',
      newSubgraphName: '',
      sourceVariantName:
        graphVariant &&
        federatedSourceVariants?.find(
          (variant) => variant.name === graphVariant,
        )
          ? graphVariant
          : '',
      description: proposalDescriptionTemplate ?? '',
    },
    onSubmit: async ({
      displayName,
      sourceVariantName,
      description,
      newSubgraphName,
    }) => {
      const { data: createProposalData, errors: createProposalErrors } =
        await createProposal({
          variables: {
            graphId,
            input: {
              displayName,
              sourceVariantName,
              description,
            },
          },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                __typename: 'Service',
                id: graphId,
              }),
              fieldName: 'proposals',
            });
          },
        });

      if (
        createProposalData?.graph?.createProposal.__typename === 'GraphVariant'
      ) {
        onClose();
        history.push(
          proposalEditorRouteConfig.path({
            graphId,
            graphVariant: createProposalData.graph.createProposal.name,
            selectedSchema: selectedSchemaFromQueryParams,
            ...(newSubgraphName ? { newSubgraphName } : {}),
          }),
        );
        return;
      }

      if (
        createProposalData?.graph?.createProposal.__typename ===
          'CreateProposalError' ||
        createProposalData?.graph?.createProposal.__typename ===
          'PermissionError' ||
        createProposalData?.graph?.createProposal.__typename ===
          'ValidationError' ||
        !createProposalData?.graph?.createProposal
      ) {
        setErrorMessage(
          createProposalData?.graph?.createProposal.message ??
            'Something went wrong trying to create this proposal. Please try again.',
        );
      } else if (isMinLengthArray(1, createProposalErrors)) {
        setErrorMessage(
          'Something went wrong trying to create this proposal. Please try again.',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            createProposalData?.graph.createProposal,
            'Something went wrong trying to create this proposal. Please try again.',
          ),
        );
      }
    },
    validationSchema,
  });

  const currentVariantSubgraphs = useMemo(() => {
    if (existingSubgraphNames instanceof Array) {
      return existingSubgraphNames;
    }
    return existingSubgraphNames?.[values.sourceVariantName];
  }, [existingSubgraphNames, values.sourceVariantName]);

  const createButtonDisabled = useMemo(
    () =>
      !(
        isValid &&
        values.displayName.length > 0 &&
        (!shouldPromptForNewSubgraphName ||
          values.newSubgraphName.length > 0) &&
        values.sourceVariantName.length > 0
      ),
    [
      isValid,
      shouldPromptForNewSubgraphName,
      values.displayName.length,
      values.newSubgraphName.length,
      values.sourceVariantName.length,
    ],
  );

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  const popperOptions: Partial<Popper.Options> = useMemo(() => {
    return {
      modifiers: [
        {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            if (state.styles.popper) {
              // eslint-disable-next-line no-param-reassign
              state.styles.popper.width = `${state.rects.reference.width}px`;
            }
          },
          effect: ({ state }) => {
            // eslint-disable-next-line no-param-reassign
            state.elements.popper.style.width = `${
              state.elements.reference.getBoundingClientRect().width
            }px`;
            // eslint-disable-next-line no-param-reassign
            state.elements.popper.className = '[&>.tippy-box]:!max-w-full';
          },
        },
      ],
    };
  }, []);

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onCloseModal}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {shouldPromptForNewSubgraphName
                ? `Propose a new subgraph to ${data?.graph?.name ?? graphId}`
                : 'Propose schema changes'}
            </ModalTitle>

            <ModalDescription>
              Schema proposals give teams a collaborative space to evolve their
              graphs. Edit subgraph schemas, start discussions, and request
              reviews from your teammates.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <Loading />
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={onCloseModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onCloseModal}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {shouldPromptForNewSubgraphName
                ? `Propose a new subgraph to ${data?.graph?.name ?? graphId}`
                : 'Propose schema changes'}
            </ModalTitle>

            <ModalDescription>
              Schema proposals give teams a collaborative space to evolve their
              graphs. Edit subgraph schemas, start discussions, and request
              reviews from your teammates.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>Could not fetch source variants</ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={onCloseModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseModal}
      size="xl"
      scrollBehavior="inside"
      finalFocusRef={finalFocusRef}
      autoFocus={!finalFocusRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {shouldPromptForNewSubgraphName
              ? `Propose a new subgraph to ${data?.graph?.name ?? graphId}`
              : 'Propose schema changes'}
          </ModalTitle>

          <ModalDescription>
            Schema proposals give teams a collaborative space to evolve their
            graphs. Edit subgraph schemas, start discussions, and request
            reviews from your teammates.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl
            isRequired
            isInvalid={!!errors.displayName && !!touched.displayName}
          >
            <FormLabel>Proposal title</FormLabel>
            <Input
              // 🚨 Do not delete or change this name / this input without updating the Proposals Datadog Browser test
              // TODO(Maya) add link to synthetics test
              data-testid="proposals_save_revision_button"
              name="displayName"
              placeholder={'e.g. "Add fields to type User"'}
              value={values.displayName}
              autoFocus
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.displayName}</FormErrorMessage>
          </FormControl>
          <FormControl
            isRequired
            isInvalid={
              !!errors.sourceVariantName && !!touched.sourceVariantName
            }
          >
            <FormLabel>Source variant</FormLabel>
            <Select
              // 🚨 Do not delete or change this name / this input without updating the Proposals Datadog Browser test
              // TODO(Maya) add link to synthetics test
              name="sourceVariantName"
              className="h-10 w-full"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.sourceVariantName}
              renderTriggerNode={() => {
                if (values.sourceVariantName) {
                  return `${graphId} / ${values.sourceVariantName}`;
                }
                return (
                  <span className="font-normal text-secondary">
                    Select the graph variant your proposal belongs to
                  </span>
                );
              }}
              // Note: The width of the popper component only is meant to fit
              // the content of its values, however it is standard for select
              // components dropdowns to match the width of the trigger node.
              // This modifier below is an adapted version of the community sameWidth
              // modifier from popper.
              // source: https://codesandbox.io/s/bitter-sky-pe3z9?file=/src/index.js
              popperOptions={popperOptions}
            >
              {federatedSourceVariants?.map((variant) => (
                <option value={variant.name} key={variant.id}>
                  {variant.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.sourceVariantName}</FormErrorMessage>
          </FormControl>
          {shouldPromptForNewSubgraphName && (
            <FormControl
              isRequired
              isInvalid={!!errors.newSubgraphName && !!touched.newSubgraphName}
            >
              <FormLabel>Subgraph name</FormLabel>
              <Input
                name="newSubgraphName"
                placeholder={'e.g. "accounts", "billing", etc.'}
                value={values.newSubgraphName}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.newSubgraphName}</FormErrorMessage>
              {!errors.newSubgraphName &&
                currentVariantSubgraphs?.includes(values.newSubgraphName) && (
                  <FormHelperText iconType="warning">
                    This subgraph already exists in this source variant. If you
                    decide to proceed, you'll be taken to that existing subgraph
                    with the ability to edit it. To propose a new subgraph,
                    please choose a different name.
                  </FormHelperText>
                )}
            </FormControl>
          )}
          <FormControl>
            <FormLabel>
              Why are you creating this proposal?
              <Tooltip
                label="This will appear in your proposal's description"
                className="max-w-max"
              >
                <IconInfo tabIndex={0} />
              </Tooltip>
            </FormLabel>
            <div className="relative">
              <Textarea
                name="description"
                size="lg"
                className="resize-y"
                value={values.description}
                placeholder={`Give your team context about your proposal, including the proposal's intent. For example, "Adding new fields to type X because of Y."`}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <IconMarkdownSolid className="absolute bottom-3 right-3 z-10 text-icon-primary" />
            </div>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              loadingText="Creating proposal"
              isDisabled={createButtonDisabled}
              type="submit"
              onClick={() => handleSubmit()}
            >
              Create proposal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
